/**
 * InsurerLetterUpload — G1.5 patient-facing letter intake.
 *
 * Renders a compact "Received a letter? Upload it here" affordance inside
 * a form's status card in FormTrackerSection. The patient can:
 *   - Tap a button to open a file/camera chooser.
 *   - Pick a photo (mobile camera) or an image/PDF from their files.
 *   - See a progress spinner while the file is prepared (downscaled if
 *     needed) and uploaded.
 *   - See a warm confirmation message on success.
 *
 * Auth: uses the bridge session stored in sessionStorage (same session the
 * magic-link flow established). If the session has expired, the patient is
 * shown a message to re-open their magic link. The bridge session is scoped
 * to the specific assignment_id — only the letter for that assignment can be
 * uploaded.
 *
 * Encryption: handled in letterUpload.js (sealAndUploadLetter). The raw file
 * bytes are sealed under the Mac's public key before transmission. The bridge
 * stores only ciphertext.
 */

import React, { useRef, useState } from "react";
import { getBridgeSession } from "../../lib/bridgeClient";
import { sealAndUploadLetter } from "../../lib/letterUpload";

const ACCEPTED_TYPES = "image/*,application/pdf";

const STATE = {
  IDLE: "idle",
  PREPARING: "preparing",   // downscaling image
  UPLOADING: "uploading",   // network request in flight
  SUCCESS: "success",
  ERROR: "error",
};

interface InsurerLetterUploadProps {
  /** Bridge assignment id (asg_xxx). Used to validate that the active session
   *  covers this assignment. */
  assignmentId?: string;
}

export default function InsurerLetterUpload({ assignmentId }: InsurerLetterUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = useState<string>(STATE.IDLE);
  const [errorMsg, setErrorMsg] = useState("");

  const session = getBridgeSession();
  // Session is scoped to one assignment. Only show the upload affordance when
  // the session matches this specific form — otherwise the endpoint will 403.
  const sessionMatchesAssignment =
    session?.assignmentId === assignmentId && session?.sessionToken && session?.pubkeyB64;

  if (!sessionMatchesAssignment) {
    // Portal session doesn't cover this assignment's bridge session — patient
    // would need to re-open their magic link. Show a soft nudge.
    return (
      <div style={{
        marginTop: 10,
        padding: "10px 14px",
        backgroundColor: "#f8f6f2",
        borderRadius: 8,
        border: "1px dashed #d8c9b6",
        fontSize: 13,
        color: "#5b6e7c",
        lineHeight: 1.5,
      }}>
        <strong style={{ color: "#182a33" }}>Received a letter from your insurer?</strong>{" "}
        Re-open your original form link to photograph and send it to your care team.
      </div>
    );
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected after an error.
    e.target.value = "";

    setErrorMsg("");
    setUploadState(STATE.PREPARING);

    try {
      setUploadState(STATE.UPLOADING);
      await sealAndUploadLetter(file, session);
      setUploadState(STATE.SUCCESS);
    } catch (err) {
      setErrorMsg((err as Error).message || "Upload failed. Please try again.");
      setUploadState(STATE.ERROR);
    }
  }

  if (uploadState === STATE.SUCCESS) {
    return (
      <div style={{
        marginTop: 10,
        padding: "10px 14px",
        backgroundColor: "#eef4f2",
        borderRadius: 8,
        border: "1px solid #bbd5cc",
        fontSize: 13,
        color: "#38534a",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <span style={{ fontSize: 16 }}>&#10003;</span>
        <span>Sent to your care team — they'll review it shortly.</span>
      </div>
    );
  }

  const isBusy = uploadState === STATE.PREPARING || uploadState === STATE.UPLOADING;

  return (
    <div style={{ marginTop: 10 }}>
      {uploadState === STATE.ERROR && errorMsg && (
        <div style={{
          marginBottom: 8,
          padding: "8px 12px",
          backgroundColor: "#faeeed",
          borderRadius: 6,
          border: "1px solid #f3d5d3",
          fontSize: 12,
          color: "#c25450",
        }}>
          {errorMsg}
        </div>
      )}

      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        backgroundColor: "#fdf9f5",
        borderRadius: 8,
        border: "1px dashed #d8c9b6",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#182a33" }}>
            Received a letter from your insurer?
          </div>
          <div style={{ fontSize: 12, color: "#5b6e7c", marginTop: 2, lineHeight: 1.4 }}>
            Photograph or upload it here and we'll get it to your care team.
          </div>
        </div>

        {/* Hidden file input. accept includes capture for mobile camera. */}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          capture="environment"
          style={{ display: "none" }}
          onChange={handleFileChange}
          aria-label="Upload insurer letter"
        />

        <button
          onClick={() => !isBusy && inputRef.current?.click()}
          disabled={isBusy}
          style={{
            padding: "7px 16px",
            fontSize: 13,
            fontWeight: 600,
            backgroundColor: isBusy ? "#d8c9b6" : "#182a33",
            color: isBusy ? "#5b6e7c" : "#fff",
            border: "none",
            borderRadius: 7,
            cursor: isBusy ? "default" : "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
            transition: "background-color 0.15s",
          }}
          aria-busy={isBusy}
        >
          {uploadState === STATE.PREPARING && "Preparing..."}
          {uploadState === STATE.UPLOADING && "Sending..."}
          {(uploadState === STATE.IDLE || uploadState === STATE.ERROR) && "Upload letter"}
        </button>
      </div>
    </div>
  );
}
